import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { integrationService } from "@/services/IntegrationService";
import { Box, Flex, Text, Spinner, Card, Button } from "@radix-ui/themes";
import { CheckCircleIcon, XCircleIcon } from "lucide-react";

export default function IntegrationCallbackPage() {
  const { currentTenant } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("Processing connection...");
  const processed = useRef(false);

  const tenantId = currentTenant?.id;

  useEffect(() => {
    if (processed.current) return;

    const code = searchParams.get("code");
    const errorParam = searchParams.get("error");
    const stateParam = searchParams.get("state");

    // If we were opened by another breeze window (the wrapper-bypass flow),
    // *that* window is authenticated and we're not — Breeze's session doesn't
    // transfer to fresh tabs. Hand the raw callback params back to the opener
    // and let it make the auth'd API call. We skip the API call here entirely.
    let opener: Window | null = null;
    try {
      opener = window.opener && !window.opener.closed ? window.opener : null;
    } catch {
      opener = null;
    }
    if (opener) {
      processed.current = true;
      try {
        opener.postMessage(
          {
            type: "oauth-callback-raw",
            code,
            error: errorParam,
            state: stateParam,
          },
          window.location.origin,
        );
      } catch {
        // Opener navigated away or is cross-origin — fall through and show the
        // user a manual fallback message.
      }
      setStatus(errorParam ? "error" : "success");
      setMessage(
        errorParam
          ? `Connection failed: ${errorParam}`
          : "Returning to Breeze…",
      );
      setTimeout(() => {
        try {
          window.close();
        } catch {
          /* close may be blocked; user closes manually */
        }
      }, 800);
      return;
    }

    // Standalone flow: no opener, do the API call ourselves. Needs tenantId.
    if (!tenantId) return;

    processed.current = true;

    const handleCallback = async () => {
      const error = errorParam;

      if (error) {
        setStatus("error");
        setMessage(`Connection failed: ${error}`);
        return;
      }

      if (!code) {
        setStatus("error");
        setMessage("No authorization code received from provider.");
        return;
      }

      // Parse provider from the state parameter
      // Standard format: Base64(tenantId:provider:redirectUri)
      // PKCE format: Base64(Base64(tenantId:provider:redirectUri):codeVerifier)
      let provider: string | null = null;
      if (stateParam) {
        try {
          let decoded = atob(stateParam);
          // Check if the decoded value is itself Base64-encoded (PKCE wraps the original state)
          // If the first segment contains non-numeric chars, it's likely inner Base64
          const parts = decoded.split(":");
          if (parts.length >= 2 && isNaN(Number(parts[0]))) {
            // PKCE format — first part is the inner Base64, decode it
            try {
              decoded = atob(parts[0]);
              const innerParts = decoded.split(":");
              if (innerParts.length >= 2) provider = innerParts[1];
            } catch {
              // Fall through to standard parsing
            }
          }
          if (!provider && parts.length >= 2) {
            provider = parts[1];
          }
        } catch {
          console.error("Failed to decode state parameter");
        }
      }

      if (!provider) {
        setStatus("error");
        setMessage("Could not determine the integration provider. Please try connecting again.");
        return;
      }

      try {
        const redirectUri = `${window.location.origin}/org/${tenantId}/settings/integrations/callback`;

        await integrationService.createConnection({
          integration_definition_id: provider,
          name: `${provider} connection`,
          auth_code: code,
          redirect_uri: redirectUri,
          state: stateParam || undefined,
          is_user_connection: false,
        });

        setStatus("success");
        setMessage("Integration connected successfully!");
        setTimeout(() => {
          navigate(`/org/${tenantId}/settings/integrations`);
        }, 2000);
      } catch (err: any) {
        console.error("Failed to complete connection:", err);
        const msg = typeof err?.response?.data === "string"
          ? err.response.data
          : err?.response?.data?.error || err?.message || "Failed to complete connection.";
        setStatus("error");
        setMessage(msg);
      }
    };

    handleCallback();
  }, [searchParams, tenantId, navigate]);

  return (
    <Box p="6">
      <Card size="3">
        <Flex direction="column" align="center" justify="center" py="8" gap="4">
          {status === "loading" && <Spinner size="3" />}
          {status === "success" && <CheckCircleIcon size={48} color="var(--green-9)" />}
          {status === "error" && <XCircleIcon size={48} color="var(--red-9)" />}

          <Text size="5" weight="bold">
            {status === "loading" ? "Connecting..." : status === "success" ? "Connected!" : "Connection Failed"}
          </Text>

          <Text size="3" color="gray" align="center">
            {message}
          </Text>

          {status !== "loading" && (
            <Button
              variant="soft"
              onClick={() => navigate(`/org/${tenantId}/settings/integrations`)}
              mt="2"
            >
              Return to Integrations
            </Button>
          )}
        </Flex>
      </Card>
    </Box>
  );
}
